import React, { Ref } from 'react';
import { Col, Row, Card, Flex } from 'antd';
import Statistic from 'antd/es/statistic/Statistic';
import PieChart from '../../../../../components/pieChart';
import { useJourneyDashboardContext } from '../context';
import { JourneyState, Shipment } from 'interfaces/journey';
import { formatDuration } from 'util/Tools';
import { routeCollectionStats, stopCount } from '../routeStats';

const Statistics = React.forwardRef<HTMLDivElement>((props, ref) => {
  const {
    shipments,
    shipmentCount,
    state,
    waypoints,
    actionsWithoutWaypoints,
    estimatedDistance,
    estimatedTime,
    collectionsById,
    kind,
  } = useJourneyDashboardContext();

  const collectionStats = routeCollectionStats(
    Object.values(collectionsById ?? {}),
    [
      ...(waypoints ?? []).flatMap((wp) => wp.actions ?? []),
      ...(actionsWithoutWaypoints ?? []),
    ],
  );
  const collectionCount = collectionStats.total;
  const actionCount = (shipmentCount ?? 0) + collectionCount;
  const hasCollections = collectionCount > 0;
  const stops = stopCount(waypoints ?? []);
  // Con transferidas sin visitar, el número no se explica solo: se detalla qué pasó con cada una.
  const transferDetail =
    collectionStats.transferred > 0
      ? [
          collectionStats.collected > 0 &&
            `${collectionStats.collected} ${
              collectionStats.collected === 1 ? 'retirada' : 'retiradas'
            }`,
          `${collectionStats.transferred} ${
            collectionStats.transferred === 1 ? 'transferida' : 'transferidas'
          } sin visitar${
            collectionStats.transferredTo.length
              ? ` a ${collectionStats.transferredTo.join(', ')}`
              : ''
          }`,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  const ungeoTotal = actionsWithoutWaypoints?.length ?? 0;
  const getStateTranslation = (state: string | undefined) => {
    switch (state) {
      case JourneyState.Draft:
        return 'Preparación';
      case JourneyState.Offered:
        return 'Ofertada';
      case JourneyState.Assigned:
        return 'Pendiente';
      case JourneyState.Accepted:
        return 'Aceptada';
      case JourneyState.Started:
        return 'En ruta';
      case JourneyState.Finished:
        return 'Finalizada';
      case JourneyState.Transfering:
        return 'Transferencia';
      default:
        return state || 'Desconocido';
    }
  };

  let sizes = [
    { title: 'Desconocido', value: 0, color: 'lightgrey' },
    { title: 'Chico', value: 0, color: '#74C2E1' },
    { title: 'Mediano', value: 0, color: '#3F7D60' },
    { title: 'Grande', value: 0, color: '#D67CA6' },
    { title: 'Personalizado', value: 0, color: '#0DB8B2' },
  ];

  if (shipments) {
    const sizeAccumulator = shipments.reduce(
      (acc, packet: Shipment) => {
        if (packet.packageSize && acc[packet.packageSize]) {
          acc[packet.packageSize].value += 1;
        }
        return acc;
      },
      [...sizes],
    );
    sizes = sizeAccumulator;
  }

  sizes = sizes.filter((size) => size.value > 0);

  let geolocalized = [
    {
      title: 'Geolocalizados',
      value: (shipmentCount ?? 0) - ungeoTotal,
      color: '#7cb305',
    },
    {
      title: 'Sin geolocalizar',
      value: ungeoTotal,
      color: '#E49273',
    },
  ];

  geolocalized = geolocalized.filter((type) => type.value > 0);

  const parsedEstimatedTime = formatDuration(estimatedTime);

  return (
    <Card ref={ref as Ref<HTMLDivElement>} bodyStyle={{ padding: '16px' }}>
      <Row>
        <Flex align="center" style={{ width: '100%' }}>
          <Col span={14}>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic title="Estado" value={getStateTranslation(state)} />
              </Col>
              <Col span={12}>
                <Statistic
                  title={
                    kind === 'collections'
                      ? 'Colectas (paradas)'
                      : hasCollections
                        ? 'Acciones (paradas)'
                        : 'Envíos (paradas)'
                  }
                  value={
                    actionCount === 0
                      ? '--- (---)'
                      : `${actionCount} (${stops})`
                  }
                  valueStyle={actionCount === 0 ? { color: 'lightgrey' } : {}}
                />
                {transferDetail && (
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {transferDetail}
                  </span>
                )}
                {hasCollections && kind !== 'collections' && (
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {shipmentCount} entregas · {collectionCount}{' '}
                    {collectionCount === 1 ? 'colecta' : 'colectas'}
                  </span>
                )}
              </Col>
              <Col span={12}>
                <Statistic
                  title="Tiempo estimado"
                  value={parsedEstimatedTime}
                  valueStyle={estimatedTime ? {} : { color: 'lightgrey' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Distancia total"
                  value={
                    estimatedDistance
                      ? `${(estimatedDistance / 1000).toFixed(2)} km`
                      : 'Desconocido'
                  }
                  valueStyle={estimatedDistance ? {} : { color: 'lightgrey' }}
                />
              </Col>
            </Row>
          </Col>
          <Col span={10}>
            <Row>
              <Col span={10}>
                <PieChart title="Tamaños" data={sizes} />
              </Col>
              <Col offset={3} span={10}>
                <PieChart title="Geolocalizados" data={geolocalized} />
              </Col>
            </Row>
          </Col>
        </Flex>
      </Row>
    </Card>
  );
});

export default Statistics;
